import { useMemo, useState, type ComponentProps } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { CrossRegionConfirm } from '../actions/CrossRegionConfirm';
import type { AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, EmailTemplate, Market } from '../dataProvider';
import {
  declaredVariablesFor,
  templateLabel,
  undeclaredPlaceholders,
} from './emailTemplatesModel';

/**
 * The email template editor (D-settings-s3-email-templates, Phase 9 S3). The flat
 * SEO-rules content-authoring shape: a subject field and a PLAIN-text body (NOT the
 * CMS rich-text editor). EDIT-ONLY (the fixed set has no create / delete). The
 * templateKey and market are read-only (the row identity); the author edits the copy
 * for that market. An "available variables" helper shows the template type's declared
 * merge fields (from the const contract, looked up by templateKey), and a SOFT
 * subset-validation WARNS when the copy uses an undeclared placeholder but still allows
 * the save (there is no publish lifecycle). A both-market edit reuses the S1
 * CrossRegionConfirm plus two per-market audited writes. Gated email_templates:write
 * (editor and super_admin); automatic audit, no forced reason.
 */

const marketToRegion = (m: Market): AccessibleRegion =>
  m === 'us' ? 'US' : 'UK';

const EmailTemplateFormInner = ({ record }: { record: EmailTemplate }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const email = String(identity?.email ?? identity?.id ?? 'unknown');

  // The sibling rows of this template key (the per-market variants), for the
  // both-market write.
  const { data: siblings } = useGetList<EmailTemplate>('emailTemplates', {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'market', order: 'ASC' },
    filter: { templateKey: record.templateKey },
  });
  const sibs = siblings ?? [];
  const canApplyBoth = sibs.length > 1;

  const declared = declaredVariablesFor(record.templateKey);

  const [subject, setSubject] = useState(record.subject);
  const [body, setBody] = useState(record.body);
  const [applyBoth, setApplyBoth] = useState(false);
  const [pendingCross, setPendingCross] = useState(false);
  const [busy, setBusy] = useState(false);

  // The soft subset-validation: the placeholders used in the copy that are NOT
  // declared for this template type. A warning, never a block (there is no publish).
  const undeclared = useMemo(
    () => undeclaredPlaceholders([subject, body], record.templateKey),
    [subject, body, record.templateKey],
  );

  const writeRows = async (rows: EmailTemplate[]) => {
    setBusy(true);
    const nowIso = new Date().toISOString();
    try {
      for (const row of rows) {
        await dataProvider.update('emailTemplates', {
          id: row.id,
          data: { subject, body, updatedBy: email, updatedAt: nowIso },
          previousData: row,
        });
      }
      notify(
        undeclared.length > 0
          ? 'Template saved (with an undeclared variable warning)'
          : 'Template saved',
        { type: undeclared.length > 0 ? 'warning' : 'success' },
      );
      setPendingCross(false);
      refresh();
    } catch {
      notify('Could not save the template', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onSave = () => {
    if (!subject.trim()) {
      notify('A subject is required', { type: 'error' });
      return;
    }
    if (applyBoth && canApplyBoth) {
      // A both-region change is confirmed (naming the regions) before committing.
      setPendingCross(true);
      return;
    }
    void writeRows([record]);
  };

  return (
    <Box sx={{ maxWidth: 820 }} data-testid="email-template-edit">
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Edit template: {templateLabel(record.templateKey)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {marketToRegion(record.market)} variant. Transactional templates are a
        fixed set: edit the copy, the type and market are fixed.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* The available variables for this template type (the const contract,
              looked up by templateKey, shared by the uk and us rows). */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Available variables
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
            data-testid="email-variables"
          >
            {declared.map((v) => (
              <Chip key={v} size="small" label={`{{${v}}}`} />
            ))}
          </Stack>

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'email-subject' }}
          />
          {/* PLAIN text body, NOT the CMS rich-text editor: an SES email is a
              backend HTML template with merge fields, not marketing prose. */}
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            multiline
            minRows={6}
            inputProps={{ 'data-testid': 'email-body' }}
          />

          {undeclared.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1 }}
              data-testid="email-undeclared-warning"
            >
              {undeclared.map((v) => `{{${v}}}`).join(', ')}{' '}
              {undeclared.length === 1 ? 'is' : 'are'} not a declared variable
              for this template. The backend will not substitute it, so it
              renders as a literal. You can still save.
            </Alert>
          )}

          {canApplyBoth && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={applyBoth}
                  onChange={(e) => setApplyBoth(e.target.checked)}
                  slotProps={
                    {
                      input: { 'data-testid': 'email-apply-both' },
                    } as ComponentProps<typeof Switch>['slotProps']
                  }
                />
              }
              label="Apply this copy to both regions"
            />
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={busy}
          data-testid="email-save"
        >
          Save
        </Button>
      </Stack>

      <CrossRegionConfirm
        isOpen={pendingCross}
        loading={busy}
        regions={sibs.map((s) => marketToRegion(s.market))}
        what="email template"
        onConfirm={() => void writeRows(sibs)}
        onClose={() => setPendingCross(false)}
      />
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const EmailTemplateEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<EmailTemplate>(
    'emailTemplates',
    { id: Number(id) },
  );
  if (isLoading || !record) return <Loading />;
  return <EmailTemplateFormInner record={record} />;
};

/** Gated entry point registered as the emailTemplates Resource's edit. */
export const EmailTemplateEdit = () => (
  <RequireAccess capability="email_templates:write">
    <EmailTemplateEditView />
  </RequireAccess>
);
